import React, { useMemo, useState } from 'react';

import type { UUID } from '@aline/common';
import { Typography } from '@mui/material';
import clsx from 'clsx';
import { mergeDeepLeft, omit } from 'ramda';
import type {
  AssignmentWithRole,
  AssignRoleDetails,
  AssignRoleRowProps,
  NewUserWithGroupOption,
  RoleUserTuple,
  UserWithGroupOption,
} from 'src/components';
import {
  AssignRoleRow,
  assignRoleRowHeightPx,
  CenteredLoader,
  newUserEmailAvatarColor,
} from 'src/components';
import { GroupKey } from 'src/components/AccountsRolesAutoComplete';
import { Apollo } from 'src/gql/generated';
import type { UserDefinedRoleSubset, UserSubset } from 'src/types';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  empty: {
    alignItems: 'center',
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    justifyContent: 'center',
  },
  list: {
    boxSizing: 'border-box',
    /**
     * Note: This is 288px, which corresponds exactly to the height of MinimalStorableList
     * that is frequently used alongside AssignRoleList.  It's probably best UI to keep
     * the heights consistent.
     */
    height: assignRoleRowHeightPx * 4,
    overflow: 'auto',
  },
}));

interface AssigneeRoleObj {
  assignee: UserSubset | null;
  assigneeRole: UserDefinedRoleSubset | null;
}

export type WorkflowAssignmentPermissions = NonNullable<
  Apollo.GetWorkflowAssignmentsQuery['workflow']
>['permissions'];

// UUID of UserDefinedRole to AssignRoleDetails.
export type UserDefinedRoleIdToDetails = Record<UUID, AssignRoleDetails>;

export interface AssignRoleListProps extends React.HTMLAttributes<HTMLElement> {
  entityId: UUID | null;
  entityType: 'Document' | 'Workflow';
  onListChange: (roleAssignmentMap: UserDefinedRoleIdToDetails) => void;
  filterBy?: 'unassigned' | 'no filter';
  initialRoleAssignmentMap?: UserDefinedRoleIdToDetails;
  /** Only these roles. Every role when omitted. */
  roleIds?: readonly UUID[];
}

export function AssignRoleList({
  className,
  entityId,
  entityType,
  filterBy = 'no filter',
  initialRoleAssignmentMap = {},
  onListChange,
  roleIds,
}: AssignRoleListProps): JSX.Element {
  const { classes } = useStyles();
  const { filteredRoleAssignments, getInitialValues, handleRowChange, loading, roleAssignmentMap } =
    useGetUnassignedRoles({
      entityId,
      entityType,
      filterBy,
      initialRoleAssignmentMap,
      onListChange,
      roleIds,
    });

  if (loading) return <CenteredLoader className={classes.list} />;

  if (!filteredRoleAssignments.length)
    return (
      <div className={clsx(classes.list, classes.empty, className)}>
        <Typography variant="body1">No roles to assign</Typography>
      </div>
    );

  return (
    <div className={clsx(classes.list, className)}>
      {filteredRoleAssignments.map((roleAssignment, index) => {
        const item = roleAssignmentMap[roleAssignment.assigneeRole.id];
        const initialUserOrEmail = getInitialValues(item);

        return (
          <AssignRoleRow
            key={index}
            assignment={roleAssignment}
            initialUserOrEmail={initialUserOrEmail}
            onChange={handleRowChange}
          />
        );
      })}
    </div>
  );
}

export function useGetUnassignedRoles({
  entityId,
  entityType,
  filterBy = 'no filter',
  initialRoleAssignmentMap = {},
  onListChange,
  roleIds,
}: {
  entityId: UUID | null;
  entityType: 'Document' | 'Workflow';
  onListChange: (roleAssignmentMap: UserDefinedRoleIdToDetails) => void;
  filterBy?: 'unassigned' | 'no filter';
  initialRoleAssignmentMap?: UserDefinedRoleIdToDetails;
  roleIds?: readonly UUID[];
}) {
  const [roleAssignmentMap, setRoleAssignmentMap] =
    useState<UserDefinedRoleIdToDetails>(initialRoleAssignmentMap);
  const { data: workflowAssignmentsData, loading: workflowAssignmentsLoading } =
    Apollo.useGetWorkflowAssignmentsQuery({
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      skip: entityType === 'Document' || !entityId,
      variables: { workflowId: entityId ?? '' },
    });
  const roleAssignments = useWorkflowRoleAssignments({
    filterBy,
    onListChange: nextRoleAssignmentMap => {
      setRoleAssignmentMap(nextRoleAssignmentMap);
      onListChange(nextRoleAssignmentMap);
    },
    roleAssignmentMap,
    roleIds,
    workflowPermissions: workflowAssignmentsData?.workflow?.permissions ?? [],
  });

  return {
    ...roleAssignments,
    loading: workflowAssignmentsLoading,
  };
}

export function useWorkflowRoleAssignments({
  filterBy = 'no filter',
  onListChange,
  roleAssignmentMap,
  roleIds,
  workflowPermissions,
}: {
  onListChange: (roleAssignmentMap: UserDefinedRoleIdToDetails) => void;
  roleAssignmentMap: UserDefinedRoleIdToDetails;
  workflowPermissions: WorkflowAssignmentPermissions;
  filterBy?: 'unassigned' | 'no filter';
  /** Only these roles. Every role when omitted. */
  roleIds?: readonly UUID[];
}) {
  const roleIdsKey = roleIds?.join(',') ?? null;
  const assignments = useMemo((): AssigneeRoleObj[] => {
    return workflowPermissions.map(
      (permission): AssigneeRoleObj => ({
        assignee: permission.account?.__typename !== 'Org' ? permission.account : null,
        assigneeRole: permission.role,
      })
    );
  }, [workflowPermissions]);
  const filteredRoleAssignments = useMemo(() => {
    const allowedRoleIds = typeof roleIdsKey === 'string' ? new Set(roleIdsKey.split(',')) : null;
    const sortedAssignments = [...assignments].sort((a, b) => {
      if (!a.assigneeRole || !b.assigneeRole) return 0;

      return a.assigneeRole.roleName.localeCompare(b.assigneeRole.roleName);
    });

    return sortedAssignments.flatMap<AssignmentWithRole>(assignment => {
      if (!assignment.assigneeRole) return [];
      if (allowedRoleIds && !allowedRoleIds.has(assignment.assigneeRole.id)) return [];

      // TS is stupid and we have to do this to type narrow userDefinedRole.
      const assignmentWithRole: AssignmentWithRole = {
        ...assignment,
        __typename: 'AssigneeRolePair',
        assigneeRole: assignment.assigneeRole,
      };

      switch (filterBy) {
        case 'unassigned': {
          const isAssigned = Boolean(assignment.assignee);
          if (isAssigned) return [];

          return [assignmentWithRole];
        }
        case 'no filter':
          return [assignmentWithRole];
      }
    });
  }, [assignments, filterBy, roleIdsKey]);

  function handleRowChange(rowTuple: RoleUserTuple): void {
    const [roleId, details] = rowTuple;

    if (!details) {
      const newRoleAssignmentMap = omit([roleId], roleAssignmentMap);
      return onListChange(newRoleAssignmentMap);
    }

    const newRoleAssignmentMap = mergeDeepLeft({ [roleId]: details }, roleAssignmentMap);
    return onListChange(newRoleAssignmentMap);
  }

  function getInitialValues(
    assignRoleDetails?: AssignRoleDetails | null
  ): AssignRoleRowProps['initialUserOrEmail'] {
    if (assignRoleDetails?.email)
      return {
        __typename: 'User',
        avatarColor: newUserEmailAvatarColor,
        displayName: assignRoleDetails.email,
        email: assignRoleDetails.email,
        firstName: '',
        group: GroupKey.Users,
        lastName: '',
      } satisfies NewUserWithGroupOption;

    if (!assignRoleDetails?.userId) return null;

    const assigneeRolePair = assignments.find(
      ({ assignee }) => assignee?.id === assignRoleDetails.userId
    );
    return assigneeRolePair?.assignee
      ? ({
          ...assigneeRolePair.assignee,
          group: GroupKey.Users,
        } satisfies UserWithGroupOption)
      : null;
  }

  return {
    filteredRoleAssignments,
    getInitialValues,
    handleRowChange,
    roleAssignmentMap,
  };
}
